// Stimulus controller for Cloudflare Turnstile
// Rules applied:
// - Use object-oriented patterns
// - Prefer modularization
// - Use descriptive method names

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container"]

  connect() {
    // Only log in development mode
    if (!this.isProduction()) {
      console.log("Turnstile controller connected")
    }
    
    // Initialize immediately to avoid delay
    this.initializeTurnstile()
    
    // Also handle Turbo navigation by listening for turbo:load events
    document.addEventListener('turbo:load', this.handleTurboLoad)
  }

  disconnect() {
    // Remove the turbo:load event listener to prevent memory leaks
    document.removeEventListener('turbo:load', this.handleTurboLoad)
    
    // Clean up any rendered widgets to prevent memory leaks
    if (window.turnstile && this.hasContainerTarget) {
      try {
        // Reset the container
        this.containerTarget.innerHTML = ''
      } catch (e) {
        if (!this.isProduction()) {
          console.error("Error cleaning up Turnstile widget:", e)
        }
      }
    }
  }
  
  // Bound method to handle Turbo navigation
  handleTurboLoad = () => {
    if (!this.isProduction()) {
      console.log("Turbo load detected, reinitializing Turnstile")
    }
    this.initializeTurnstile()
  }

  initializeTurnstile() {
    // Check if Turnstile script is already loaded
    if (window.turnstile) {
      if (!this.isProduction()) {
        console.log("Turnstile already loaded, rendering widget")
      }
      this.renderWidget()
    } else {
      // If not loaded, load it and then render
      if (!this.isProduction()) {
        console.log("Turnstile not loaded, loading script")
      }
      this.loadTurnstileScript()
        .then(() => {
          this.renderWidget()
        })
        .catch(error => {
          if (!this.isProduction()) {
            console.error("Failed to load Turnstile script:", error)
          }
          // Add fallback for mobile devices if script fails to load
          this.addFallbackInput()
        })
    }
  }

  renderWidget() {
    // Clear any existing widgets first to prevent duplicates
    if (this.hasContainerTarget && this.containerTarget.innerHTML !== '') {
      this.containerTarget.innerHTML = ''
    }

    // Render the widget if turnstile is available
    if (window.turnstile && this.hasContainerTarget) {
      try {
        if (!this.isProduction()) {
          console.log("Rendering Turnstile widget with sitekey:", this.containerTarget.dataset.sitekey)
        }
        
        // Get the theme from data attribute or detect from system preference
        const prefersDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
        const theme = this.containerTarget.dataset.theme || (prefersDarkMode ? 'dark' : 'light')
        
        // Explicitly set size for better mobile compatibility
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
        const size = isMobile ? 'compact' : 'normal'
        
        window.turnstile.render(this.containerTarget, {
          sitekey: this.containerTarget.dataset.sitekey,
          theme: theme,
          size: size,
          retry: 'auto',
          execution: 'render',
          refresh_expired: 'auto',
          callback: (token) => {
            if (!this.isProduction()) {
              console.log("Turnstile callback received")
            }
            // Create/update the hidden input field with the token
            this.updateHiddenInput(token)
          }
        })
      } catch (e) {
        if (!this.isProduction()) {
          console.error("Error rendering Turnstile widget:", e)
        }
        this.addFallbackInput()
      }
    } else {
      if (!this.isProduction()) {
        console.error("Failed to render Turnstile widget:", { 
          turnstileAvailable: !!window.turnstile,
          containerTargetAvailable: this.hasContainerTarget,
          containerElement: this.hasContainerTarget ? this.containerTarget : null
        })
      }
      this.addFallbackInput()
    }
  }

  loadTurnstileScript() {
    return new Promise((resolve, reject) => {
      // Check if script is already in process of loading
      const existingScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')
      
      if (existingScript) {
        // Script is loading, wait for it to be ready with timeout
        let attempts = 0
        const maxAttempts = 50 // 5 seconds max wait
        
        const checkTurnstile = setInterval(() => {
          attempts++
          if (window.turnstile) {
            clearInterval(checkTurnstile)
            resolve()
          } else if (attempts >= maxAttempts) {
            clearInterval(checkTurnstile)
            reject(new Error("Turnstile script load timeout"))
          }
        }, 100)
      } else {
        // Script not loading yet, add it
        if (!this.isProduction()) {
          console.log("Loading Turnstile script dynamically")
        }
        const script = document.createElement('script')
        
        // Use render-explicit mode for better control
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=turnstileOnLoad'
        script.async = true
        script.defer = true
        script.setAttribute('data-turbo-track', 'reload')
        
        // Add error handling for script loading
        script.onerror = () => {
          reject(new Error("Failed to load Turnstile script"))
        }
        
        // Define callback for when script loads
        window.turnstileOnLoad = () => {
          if (!this.isProduction()) {
            console.log("Turnstile script loaded")
          }
          resolve()
        }
        
        document.head.appendChild(script)
      }
    })
  }

  updateHiddenInput(token) {
    // Find existing input or create a new one
    let input = document.querySelector('input[name="cf-turnstile-response"]')
    
    if (!input) {
      input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'cf-turnstile-response'
      
      // Add the input near the container
      if (this.hasContainerTarget) {
        this.containerTarget.parentNode.appendChild(input)
      } else {
        // Fallback to form
        const form = document.querySelector('form')
        if (form) form.appendChild(input)
      }
    }
    
    // Update the token value
    input.value = token
  }

  addFallbackInput() {
    // Only add fallback in production as a last resort
    if (this.isProduction() && this.hasContainerTarget) {
      if (!this.isProduction()) {
        console.warn("Adding fallback Turnstile input due to rendering failure")
      }
      
      // Clear the container
      this.containerTarget.innerHTML = ''
      
      // Add a message and hidden input
      const message = document.createElement('div')
      message.className = 'text-sm text-zinc-500 dark:text-zinc-400 p-2 border border-zinc-300 dark:border-zinc-700 rounded'
      message.innerHTML = 'Bot protection verification is having trouble loading. Please try refreshing the page.'
      
      // Create hidden input with a special value that the server will recognize
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'cf-turnstile-response'
      input.value = 'mobile-fallback'
      
      this.containerTarget.appendChild(message)
      this.containerTarget.appendChild(input)
    }
  }

  isProduction() {
    // Check if we're in production environment
    return !document.documentElement.hasAttribute('data-development') && 
           !document.documentElement.hasAttribute('data-test')
  }
}
